// Libraries
import {Col, Table, Image, Space, Tag, Switch, Carousel} from 'antd';
import {useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';

// Types
import {FiltersPaginationType} from "../../types/SharedTypes";

// Utils
import tableColumns from "../../utils/tableColumns";
import {routes} from "../../utils/routes";
import {invertColor} from "../../utils/generalUtils";

// Store
import {useAppDispatch} from '../../store/store';
import {activateCatalog} from "../../store/redux/actions/catalogActions";
import * as catalogSelectors from "../../store/redux/selectors/catalogSelectors";

type ProductListType = {
  pagination: FiltersPaginationType;
  onChangePagination: (page: number, pageSize: number) => void;
};
const ProductList = (props: ProductListType) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const {rows, count} = useSelector(catalogSelectors.allCatalogSelector)!;

  const getProductDetailHref = (supermodel: string) => {
    const detailUrl = routes(t).catalog.detail.replace(':supermodel', supermodel);
    const searchParams = new URLSearchParams();

    Object.entries(props.pagination.filterVm ?? {}).forEach(([filterName, filterValue]) => {
      const value = serializeFilterValue(filterValue);
      if (value !== undefined) searchParams.set(filterName, value);
    });

    const queryString = searchParams.toString();
    return queryString ? `${detailUrl}?${queryString}` : detailUrl;
  };

  const serializeFilterValue = (filterValue: unknown): string | undefined => {
    if (filterValue === undefined || filterValue === null || filterValue === '') return undefined;
    if (typeof filterValue === 'string') return filterValue;
    if (typeof filterValue === 'number' || typeof filterValue === 'boolean') return `${filterValue}`;

    const serializedValue = JSON.stringify(filterValue);
    return serializedValue === undefined ? undefined : serializedValue;
  };

  const dataBuilder = () => {
    let data: any = [];

    rows.map(product => {
      const productImages = product.imageUrls?.length ? product.imageUrls : [product.imageUrl];

      return data.push({
        model: product.model,
        isActive: product.isActive,
        firstColumn:
          <Col md={24} xs={24} style={{ width: 200, maxWidth: 200, minWidth: 200 }} className={' block'}>
            <Carousel arrows={true} autoplay={false} dots={true} >
              {productImages.map((imageUrl, index) => (
                <Image
                  key={`${product.model}-${index}`}
                  src={imageUrl}
                  width={200}
                  height={200}
                  preview={true}
                />
              ))}
            </Carousel>
          </Col>,
        secondColumn:
          <Col md={24} xs={24} className={'relative my-2'}>
            <Switch className={'absolute right-0 top-0 z-50'}
                    checked={product.isActive}
                    onChange={(isActive) => dispatch(activateCatalog({supermodel: product.supermodel, isActive}, 'list', props.pagination))}
            />
            <Space direction={'vertical'} size={'small'}>
              <Col md={24} xs={24} className={'text-lg -mb-2 text-blue-dkt-500'}>
                {product.brand}
              </Col>
              <a className={'font-bold text-lg cursor-pointer hover:underline hover:decoration-2'}
                  href={getProductDetailHref(product.supermodel)}>
                {product.name}
              </a>
              <Col md={24}>
                {product.alliances.map(alliance => {
                    let colorInverted = invertColor(alliance.color);
                    return <Tag style={{ backgroundColor: alliance.color, color: colorInverted}} className={`border-none my-1`}
                                key={alliance.allianceId}
                                closable={false}>
                              {alliance.name}
                            </Tag>
                  }
                )}
              </Col>
              <Space size={'large'}>
                <Col>
                  <span className={'font-semibold text-sm'}>{t('supermodel')}: </span> {`${product.supermodel ?? '-'}`}
                </Col>
                <Col>
                  <span className={'font-semibold text-sm'}>{t('model')}: </span> {`${product.models.join(' - ') ?? '-'}`}
                </Col>
                <Col>
                  <span className={'font-semibold text-sm'}>{t('codes')}: </span> {`${product.codes.join(' - ') ?? '-'}`}
                </Col>
              </Space>
            </Space>
          </Col>
      })
  });

    return data;
  };

  return (
        <Table scroll={{ x: 700 }} showHeader={false} rowKey={'model'} size={'small'}
           columns={tableColumns('catalog')} dataSource={dataBuilder()}
           rowClassName={(record: any) => record.isActive ? '' : 'inactive-product-row'}
           pagination={{
             pageSize: props.pagination.paginationVm.size,
             current: props.pagination.paginationVm.page,
             showSizeChanger: true,
             total: count,
             onChange: (page, pageSize) => props.onChangePagination(page, pageSize)
           }}
    />
  );
}

export default ProductList;